import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { port } from '../../App';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MetricCard from '../../Components/ActivityComponents/MetricCard';
import DetailsModal from '../../Components/ActivityComponents/DetailsModal';
import DashboardFilter from '../../Components/ActivityComponents/DashboardFilter';
import PendingFollowUpsModal from '../../Components/ActivityComponents/PendingFollowUpsModal';
import CompletedFollowUpsModal from '../../Components/ActivityComponents/CompletedFollowUpsModal';
// import RequirementAnalysisModal from '../../Components/Modals/RequirementAnalysisModal';

const Loader = ({ height = '200px' }) => (
    <div className="flex justify-center items-center" style={{ height }}>
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
    </div>
);

// 29/04/2026
const LeadActivityCard = ({ metrics, onCardClick }) => {
    return (
        <div className="card shadow-sm border-0 mb-4 overflow-hidden font-popins"
            style={{
                background: '#ffffff',
                borderRadius: '12px'
            }}>
            <div className="card-header border-0 bg-transparent pt-3 pb-0">
                <h6 className="font-weight-bold text-uppercase mb-0" style={{ color: '#2b2d31', fontSize: '0.75rem', letterSpacing: '1px' }}>
                    Lead Activity Details
                </h6>
            </div>
            <div className="card-body p-0">
                <div className="row g-0 text-center">
                    {[
                        { label: 'Total Calls', value: metrics?.lead_total_calls || 0, type: 'lead-total-calls', glow: '#3b82f6', icon: 'fa-phone-volume' },
                        { label: 'Interview', value: metrics?.lead_interview_calls || 0, type: 'lead-interview-calls', glow: '#10b981', icon: 'fa-user-tie' },
                        { label: 'Client', value: metrics?.lead_client_calls || 0, type: 'lead-client-calls', glow: '#f59e0b', icon: 'fa-building' },
                        { label: 'Fresh', value: metrics?.lead_fresh_calls || 0, type: 'lead-fresh-calls', glow: '#8b5cf6', icon: 'fa-sparkles' },
                        { label: 'Follow UP Done', value: metrics?.lead_followup_calls || 0, type: 'lead-followup-calls', glow: '#06b6d4', icon: 'fa-check-double' }
                    ].map((item, index) => (
                        <div key={index}
                            className={`col p-4 ${index !== 0 ? 'border-start' : ''}`}
                            style={{
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                borderLeftColor: '#f1f5f9'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#f8fafc';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = `0 4px 6px -1px rgb(0 0 0 / 0.1)`;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                            onClick={() => onCardClick(item.type)}>
                            <div className="mb-2" style={{ color: '#64748b', fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                {item.label}
                            </div>
                            <div className="d-flex align-items-center justify-content-center gap-2">
                                <h3 className="mb-0 fw-bold" style={{ color: '#1e293b', fontSize: '1.75rem' }}>{item.value}</h3>
                            </div>
                            <div className="mt-2" style={{ height: '3px', width: '30px', background: item.glow, margin: '0 auto', borderRadius: '10px', opacity: '0.6' }}></div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const ActivityDashboard = ({ targetEmpId, employeeName, headerLeft, headerRight }) => {
    const empid = JSON.parse(sessionStorage.getItem('user'))?.EmployeeId;

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Derive filters directly from URL (Single Source of Truth)
    const filterType = searchParams.get('filter_type') || 'today';
    const customStartDate = searchParams.get('start_date') || '';
    const customEndDate = searchParams.get('end_date') || '';
    const selectedRequirementId = searchParams.get('requirement_id') || '';
    const startTime = searchParams.get('start_time') || '';
    const endTime = searchParams.get('end_time') || '';

    // Fetch active requirements for dropdown filter
    const [requirements, setRequirements] = useState([]);
    useEffect(() => {
        if (!empid) return;
        axios.get(`${port}/root/activity/requirement-performance?login_emp_id=${empid}&page_size=100`)
            .then(res => {
                setRequirements(res.data.results || []);
            })
            .catch(err => console.error("Error fetching requirements for dropdown:", err));
    }, [empid]);

    // URL updating functions
    const setSelectedRequirementId = (reqId) => {
        const newParams = new URLSearchParams(searchParams);
        if (reqId) {
            newParams.set('requirement_id', reqId);
        } else {
            newParams.delete('requirement_id');
        }
        setSearchParams(newParams);
    };

    const setFilterType = (newType) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('filter_type', newType);
        if (newType !== 'custom') {
            newParams.delete('start_date');
            newParams.delete('end_date');
        }
        setSearchParams(newParams);
    };

    const setCustomStartDate = (date) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('start_date', date);
        setSearchParams(newParams);
    };

    const setCustomEndDate = (date) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('end_date', date);
        setSearchParams(newParams);
    };

    const setStartTime = (time) => {
        const newParams = new URLSearchParams(searchParams);
        if (time) {
            newParams.set('start_time', time);
        } else {
            newParams.delete('start_time');
        }
        setSearchParams(newParams);
    };

    const setEndTime = (time) => {
        const newParams = new URLSearchParams(searchParams);
        if (time) {
            newParams.set('end_time', time);
        } else {
            newParams.delete('end_time');
        }
        setSearchParams(newParams);
    };

    // Helper to construct details page URL forwarding filters
    const getDetailsUrl = (metricType) => {
        const urlType = metricType.replace(/_/g, '-');
        const queryParams = new URLSearchParams();
        if (targetEmpId) {
            queryParams.append('target_emp_id', targetEmpId);
            if (employeeName) {
                queryParams.append('target_emp_name', employeeName);
            }
        }
        queryParams.append('filter_type', filterType);
        if (filterType === 'custom' && customStartDate && customEndDate) {
            queryParams.append('start_date', customStartDate);
            queryParams.append('end_date', customEndDate);
        }
        if (selectedRequirementId) {
            queryParams.append('requirement_id', selectedRequirementId);
        }
        if (startTime) queryParams.append('start_time', startTime);
        if (endTime) queryParams.append('end_time', endTime);
        return `/activity/details/${urlType}?${queryParams.toString()}`;
    };


    const [dashboardData, setDashboardData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalData, setModalData] = useState([]);
    const [isModalLoading, setIsModalLoading] = useState(false);

    const [isPendingFollowUpsModalOpen, setIsPendingFollowUpsModalOpen] = useState(false);
    const [isCompletedFollowUpsModalOpen, setIsCompletedFollowUpsModalOpen] = useState(false);
    const [isOutcomeModalOpen, setIsOutcomeModalOpen] = useState(false);
    const [isTotalCallsModalOpen, setIsTotalCallsModalOpen] = useState(false);
    const [pendingFollowUpsData, setPendingFollowUpsData] = useState([]);
    const [completedFollowUpsData, setCompletedFollowUpsData] = useState([]);
    const [isFollowUpLoading, setIsFollowUpLoading] = useState(false);
    // const [isReqModalOpen, setIsReqModalOpen] = useState(false);

    const fetchDashboardData = () => {
        if (!empid) return;
        setIsLoading(true);
        const params = new URLSearchParams({ login_emp_id: empid, filter_type: filterType });
        if (targetEmpId) {
            params.append('target_emp_id', targetEmpId);
        }
        if (selectedRequirementId) {
            params.append('requirement_id', selectedRequirementId);
        }
        if (filterType === 'custom' && customStartDate && customEndDate) {
            params.append('start_date', customStartDate);
            params.append('end_date', customEndDate);
        }
        if (startTime) params.append('start_time', startTime);
        if (endTime) params.append('end_time', endTime);
        axios.get(`${port}/root/activity-dashboard-analytics?${params.toString()}`)
            .then(response => setDashboardData(response.data))
            .catch(error => console.error("Error fetching dashboard data:", error))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        if (filterType === 'custom' && (!customStartDate || !customEndDate)) return;
        fetchDashboardData();
    }, [filterType, customStartDate, customEndDate, targetEmpId, selectedRequirementId, startTime, endTime]);



    // const handleRedirect = (aid) => {
    //     const date = new Date();
    //     let month = date.getMonth(); // 0-11
    //     let year = date.getFullYear();

    //     if (filterType === 'prev_month') {
    //         month -= 1;
    //         if (month < 0) {
    //             month = 11;
    //             year -= 1;
    //         }
    //     }

    //     const employeeIdToUse = targetEmpId || empid;
    //     navigate(`/activity/particularActivity/${employeeIdToUse}?aid=${aid}&month=${month}&year=${year}`);
    // };

    // New handlers for follow-up modals
    const fetchPendingFollowUps = () => {
        if (!empid) return;
        setIsFollowUpLoading(true);
        const params = new URLSearchParams({ login_emp_id: empid, filter_type: filterType });
        if (targetEmpId) {
            params.append('target_emp_id', targetEmpId);
        }
        if (filterType === 'custom' && customStartDate && customEndDate) {
            params.append('start_date', customStartDate);
            params.append('end_date', customEndDate);
        }
        axios.get(`${port}/root/activity/pending-followups?${params.toString()}`)
            .then(response => setPendingFollowUpsData(response.data.pending_followups || []))
            .catch(error => { console.error("Error fetching pending follow-ups:", error); setPendingFollowUpsData([]); })
            .finally(() => setIsFollowUpLoading(false));
    };

    const fetchCompletedFollowUps = () => {
        if (!empid) return;
        setIsFollowUpLoading(true);
        const params = new URLSearchParams({ login_emp_id: empid, filter_type: filterType });
        if (targetEmpId) {
            params.append('target_emp_id', targetEmpId);
        }
        if (filterType === 'custom' && customStartDate && customEndDate) {
            params.append('start_date', customStartDate);
            params.append('end_date', customEndDate);
        }
        axios.get(`${port}/root/activity/completed-followups?${params.toString()}`)
            .then(response => setCompletedFollowUpsData(response.data.completed_followups || []))
            .catch(error => { console.error("Error fetching completed follow-ups:", error); setCompletedFollowUpsData([]); })
            .finally(() => setIsFollowUpLoading(false));
    };

    const handleCardClick = (metricType, title) => {
        // Intercept Successful Outcomes for the drilldown modal
        if (metricType === 'successful_outcomes') {
            setIsOutcomeModalOpen(true);
            return;
        }

        // Intercept Total Calls Made for the drilldown modal
        if (metricType === 'total_activities' || metricType === 'lead-total-calls') {
            setIsTotalCallsModalOpen(true);
            return;
        }

        navigate(getDetailsUrl(metricType));

        /* COMMENTED OUT OLD MODAL LOGIC
        // Handle new follow-up metric types
        if (metricType === 'pending_followups') {
            setIsPendingFollowUpsModalOpen(true);
            fetchPendingFollowUps();
            return;
        }
        if (metricType === 'completed_followups') {
            setIsCompletedFollowUpsModalOpen(true);
            fetchCompletedFollowUps();
            return;
        }

        if (metricType === 'requirement_performance') {
            setIsReqModalOpen(true);
            return;
        }

        setModalTitle(title);
        setIsModalOpen(true);
        setIsModalLoading(true);
        const params = new URLSearchParams({ login_emp_id: empid, filter_type: filterType, metric_type: metricType });
        if (targetEmpId) {
            params.append('target_emp_id', targetEmpId);
        }
        if (filterType === 'custom' && customStartDate && customEndDate) {
            params.append('start_date', customStartDate);
            params.append('end_date', customEndDate);
        }
        axios.get(`${port}/root/activity-dashboard-details?${params.toString()}`)
            .then(response => setModalData(response.data))
            .catch(error => { console.error(`Error fetching details for ${metricType}:`, error); setModalData([]); })
            .finally(() => setIsModalLoading(false));
        */
    };

    return (
        <div className="container-fluid px-2 py-0">
            <DashboardFilter
                filterType={filterType}
                setFilterType={setFilterType}
                customStartDate={customStartDate}
                setCustomStartDate={setCustomStartDate}
                customEndDate={customEndDate}
                setCustomEndDate={setCustomEndDate}
                selectedRequirementId={selectedRequirementId}
                setSelectedRequirementId={setSelectedRequirementId}
                requirements={requirements}
                startTime={startTime}
                setStartTime={setStartTime}
                endTime={endTime}
                setEndTime={setEndTime}
                employeeName={employeeName}
                headerLeft={headerLeft}
                headerRight={headerRight}
            />


            {isLoading ? (
                <Loader />
            ) : (
                dashboardData && (
                    <>
                        {/* <div className="row"> */}
                            {/* <MetricCard title="Total Calls Made" value={dashboardData.metrics?.lead_total_calls || 0} icon="fa-tasks" metricType="total_activities" onClick={handleCardClick} />
                            <MetricCard title="Successful Outcomes" value={dashboardData.metrics?.successful_outcomes_count || 0} icon="fa-check-circle" metricType="successful_outcomes" onClick={handleCardClick} /> */}

                            {/* Old Follow Ups card - replaced with Pending and Completed */}
                            {/* <MetricCard title="Follow Ups" value={dashboardData.metrics?.follow_ups_count || 0} icon="fa-history" metricType="follow_ups" onClick={handleCardClick} /> */}

                            {/* New Follow-Up Enhancement - Separate Pending and Completed */}
                            {/* <MetricCard title="Pending Follow Ups" value={dashboardData.metrics?.pending_followups_count || 0} icon="fa-clock" metricType="pending_followups" onClick={handleCardClick} />
                            <MetricCard title="Follow Ups Done" value={dashboardData.metrics?.completed_followups_count || 0} icon="fa-check-double" metricType="completed_followups" onClick={handleCardClick} /> */}

                            {/* Old Rejected/Closed combined card - replaced with separate cards */}
                            {/* <MetricCard title="Rejected / Closed" value={dashboardData.metrics?.rejected_closed_count || 0} icon="fa-times-circle" metricType="rejected_closed" onClick={handleCardClick} /> */}

                            {/* New Follow-Up Enhancement - Separate Rejected and Closed */}
                            {/* <MetricCard title="Rejected" value={dashboardData.metrics?.rejected_count || 0} icon="fa-ban" metricType="rejected" onClick={handleCardClick} />
                            <MetricCard title="Closed" value={dashboardData.metrics?.closed_count || 0} icon="fa-times-circle" metricType="closed" onClick={handleCardClick} />

                            <MetricCard title="Interview Calls" value={dashboardData.metrics?.lead_interview_calls || 0} icon="fa-phone-alt" metricType="interview_calls" onClick={handleCardClick} />
                            <MetricCard title="Client Calls" value={dashboardData.metrics?.lead_client_calls || 0} icon="fa-headset" metricType="client_calls" onClick={handleCardClick} />
                            <MetricCard title="Job Posts" value={dashboardData.metrics?.job_posts_count || 0} icon="fa-bullhorn" metricType="job_posts" onClick={handleCardClick} />
                            <MetricCard title="Active Requirements" value={dashboardData.requirement_breakdown?.length || 0} icon="fa-briefcase" metricType="requirement_performance" onClick={handleCardClick} /> */}
                        {/* </div> */}

                        {/* <div className="mt-4">
                            <LeadActivityCard metrics={dashboardData.metrics} onCardClick={handleCardClick} />
                        </div> */}


                        {/* COMPREHENSIVE ANALYTICS PANELS */}
                        <ComprehensiveAnalytics
                            metrics={dashboardData.metrics}
                            onNavigate={(type) => {
                                if (type === 'profiles-facebook') {
                                    navigate('/activity/other-leads');
                                } else {
                                    navigate(getDetailsUrl(type));
                                }
                            }}
                        />
                    </>
                )
            )}

            {/* COMMENTED OUT OLD MODALS
            <DetailsModal
                show={isModalOpen}
                onHide={() => setIsModalOpen(false)}
                title={modalTitle}
                data={modalData}
                isLoading={isModalLoading}
                onRefresh={fetchDashboardData} // Refresh dashboard after converting leads
            />

            <PendingFollowUpsModal
                show={isPendingFollowUpsModalOpen}
                onHide={() => setIsPendingFollowUpsModalOpen(false)}
                data={pendingFollowUpsData}
                isLoading={isFollowUpLoading}
                onRefresh={() => {
                    fetchPendingFollowUps();
                    fetchDashboardData(); // Refresh dashboard counts
                }}
            />

            <CompletedFollowUpsModal
                show={isCompletedFollowUpsModalOpen}
                onHide={() => setIsCompletedFollowUpsModalOpen(false)}
                data={completedFollowUpsData}
                isLoading={isFollowUpLoading}
            />
            */}
            {/* 
            <RequirementAnalysisModal
                show={isReqModalOpen}
                onHide={() => setIsReqModalOpen(false)}
                data={dashboardData?.requirement_breakdown || []}
            />
            */}

            {/* Outcome Drilldown Modal */}
            <OutcomeDrilldownModal
                isOpen={isOutcomeModalOpen}
                onClose={() => setIsOutcomeModalOpen(false)}
                metrics={dashboardData?.metrics}
                onSelect={(type) => {
                    setIsOutcomeModalOpen(false);
                    navigate(getDetailsUrl(type));
                }}
            />

            {/* Total Calls Drilldown Modal */}
            <TotalCallsDrilldownModal
                isOpen={isTotalCallsModalOpen}
                onClose={() => setIsTotalCallsModalOpen(false)}
                metrics={dashboardData?.metrics}
                requirementBreakdownCount={dashboardData?.requirement_breakdown?.length || 0}
                onSelect={(type) => {
                    setIsTotalCallsModalOpen(false);
                    navigate(getDetailsUrl(type));
                }}
            />
        </div>
    );
};

// Outcome Drilldown Modal Component
const OutcomeDrilldownModal = ({ isOpen, onClose, metrics, onSelect }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1050] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200" style={{ animation: 'zoomIn 0.2s ease-out' }}>
                <div className="p-4 border-b d-flex justify-between align-items-center bg-slate-50">
                    <div className="text-left">
                        <h5 className="text-xl font-bold text-slate-800 mb-0">Outcome Breakdown</h5>
                        <p className="text-sm text-slate-500 mb-0">Select a category to view details</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors border-0 bg-transparent">
                        <i className="fas fa-times text-slate-400 text-xl"></i>
                    </button>
                </div>

                <div className="p-5">
                    <div className="row g-4">
                        {/* Selected Candidates Card */}
                        <div className="col-md-6">
                            <div
                                onClick={() => onSelect('selected-candidates')}
                                className="p-5 rounded-xl border-2 border-slate-100 hover:border-blue-500 hover:bg-blue-50 transition-all duration-300 transform hover:-translate-y-1 shadow-sm cursor-pointer text-center"
                            >
                                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mx-auto mb-3">
                                    <i className="fas fa-user-check text-2xl"></i>
                                </div>
                                <h6 className="font-bold text-slate-700 text-lg mb-1">Selected</h6>
                                <div className="d-flex align-items-baseline justify-content-center gap-2">
                                    <span className="text-3xl font-extrabold text-slate-800">{metrics?.selected_count || 0}</span>
                                    <span className="text-slate-500 font-medium small">Candidates</span>
                                </div>
                            </div>
                        </div>

                        {/* Joined Candidates Card */}
                        <div className="col-md-6">
                            <div
                                onClick={() => onSelect('joined-candidates')}
                                className="p-5 rounded-xl border-2 border-slate-100 hover:border-emerald-500 hover:bg-emerald-50 transition-all duration-300 transform hover:-translate-y-1 shadow-sm cursor-pointer text-center"
                            >
                                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 mx-auto mb-3">
                                    <i className="fas fa-handshake text-2xl"></i>
                                </div>
                                <h6 className="font-bold text-slate-700 text-lg mb-1">Joined</h6>
                                <div className="d-flex align-items-baseline justify-content-center gap-2">
                                    <span className="text-3xl font-extrabold text-slate-800">{metrics?.joined_count || 0}</span>
                                    <span className="text-slate-500 font-medium small">Candidates</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-4 bg-slate-50 border-t d-flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold rounded-lg transition-colors border-0"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// Total Calls Drilldown Modal Component
const TotalCallsDrilldownModal = ({ isOpen, onClose, metrics, requirementBreakdownCount, onSelect }) => {
    if (!isOpen) return null;

    const categories = [
        { label: 'Client Calls', count: metrics?.lead_client_calls || 0, type: 'lead-client-calls', icon: 'fa-phone-alt', color: 'blue' },
        { label: 'Interview Calls', count: metrics?.lead_interview_calls || 0, type: 'lead-interview-calls', icon: 'fa-user-tie', color: 'emerald' },
        { label: 'Follow Ups Done', count: metrics?.completed_followups_count || 0, type: 'lead-followup-calls', icon: 'fa-check-double', color: 'cyan' },
        { label: 'Screening', count: metrics?.screening_count || 0, type: 'screening-calls', icon: 'fa-filter', color: 'purple' },
        { label: 'Scheduled', count: metrics?.scheduled_count || 0, type: 'interview-scheduled', icon: 'fa-calendar-alt', color: 'indigo' },
        { label: 'Job Posts', count: metrics?.job_posts_count || 0, type: 'job-posts', icon: 'fa-bullhorn', color: 'orange' },
        { label: 'Rejected', count: metrics?.rejected_count || 0, type: 'rejected', icon: 'fa-ban', color: 'red' },
        { label: 'Closed', count: metrics?.closed_count || 0, type: 'closed', icon: 'fa-times-circle', color: 'slate' }
    ];

    return (
        <div className="fixed inset-0 z-[1050] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden animate-in zoom-in-95 duration-200" style={{ animation: 'zoomIn 0.2s ease-out' }}>
                <div className="p-4 border-b d-flex justify-between align-items-center bg-slate-50">
                    <div className="text-left">
                        <h5 className="text-xl font-bold text-slate-800 mb-0">Total Activity Breakdown</h5>
                        <p className="text-sm text-slate-500 mb-0">Detailed breakdown of all calls and activities</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors border-0 bg-transparent">
                        <i className="fas fa-times text-slate-400 text-xl"></i>
                    </button>
                </div>

                <div className="p-4 max-h-[70vh] overflow-y-auto">
                    <div className="row g-3">
                        {categories.map((cat, idx) => (
                            <div key={idx} className="col-md-3 col-sm-6">
                                <div
                                    onClick={() => onSelect(cat.type)}
                                    className={`p-4 rounded-xl border-2 border-slate-100 hover:border-${cat.color}-500 hover:bg-${cat.color}-50 transition-all duration-300 transform hover:-translate-y-1 shadow-sm cursor-pointer text-center h-full flex flex-col items-center justify-center`}
                                >
                                    <div className={`w-12 h-12 rounded-full bg-${cat.color}-100 flex items-center justify-center text-${cat.color}-600 mb-2`}>
                                        <i className={`fas ${cat.icon} text-xl`}></i>
                                    </div>
                                    <h6 className="font-bold text-slate-700 text-sm mb-1">{cat.label}</h6>
                                    <div className="d-flex align-items-baseline gap-1">
                                        <span className="text-2xl font-extrabold text-slate-800">{cat.count}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="p-4 bg-slate-50 border-t d-flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold rounded-lg transition-colors border-0"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// =====================================================
// COMPREHENSIVE ANALYTICS COMPONENT (28/05/2026)
// =====================================================
const ComprehensiveAnalytics = ({ metrics, onNavigate }) => {
    const m = metrics || {};

    const SectionPanel = ({ title, icon, color, colorClass, children, isTwoLine }) => (
        <div style={{
            background: '#fff',
            borderRadius: '16px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
            overflow: 'hidden',
            border: '1px solid #f1f5f9'
        }}>
            {/* Panel Header */}
            <div style={{
                background: `linear-gradient(135deg, ${color}15, ${color}08)`,
                borderBottom: `3px solid ${color}`,
                padding: '14px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
            }}>
                <div style={{
                    width: '34px', height: '34px',
                    borderRadius: '8px',
                    background: color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <i className={`fas ${icon}`} style={{ color: '#fff', fontSize: '14px' }}></i>
                </div>
                <h6 style={{ margin: 0, fontWeight: 700, color: '#1e293b', fontSize: '0.8rem', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                    {title}
                </h6>
            </div>
            {/* Panel Body */}
            <div style={{ padding: '16px' }}>
                {isTwoLine ? (
                    children
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px' }}>
                        {children}
                    </div>
                )}
            </div>
        </div>
    );

    const StatBadge = ({ label, value, metricType, accent, icon, isReadOnly }) => (
        <div
            onClick={isReadOnly ? null : () => onNavigate(metricType)}
            title={isReadOnly ? label : `View ${label}`}
            style={{
                background: '#f8fafc',
                border: `1.5px solid #e2e8f0`,
                borderRadius: '10px',
                padding: '12px 10px',
                cursor: isReadOnly ? 'default' : 'pointer',
                transition: 'all 0.2s ease',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden'
            }}
            onMouseEnter={e => {
                if (isReadOnly) return;
                e.currentTarget.style.borderColor = accent;
                e.currentTarget.style.background = `${accent}10`;
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 4px 12px ${accent}25`;
            }}
            onMouseLeave={e => {
                if (isReadOnly) return;
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.background = '#f8fafc';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
            }}
        >
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1e293b', lineHeight: 1, marginBottom: '4px' }}>
                {value ?? 0}
            </div>
            <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600, lineHeight: 1.3 }}>
                {label}
            </div>
            <div style={{ height: '2px', background: accent, borderRadius: '4px', marginTop: '8px', opacity: 0.6 }} />
        </div>
    );

    return (
        <div style={{ marginTop: '24px' }}>
            {/* Section Title */}
            {/* <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ height: '1px', flex: 1, background: 'linear-gradient(to right, #e2e8f0, transparent)' }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '1.5px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    Detailed Analytics
                </span>
                <div style={{ height: '1px', flex: 1, background: 'linear-gradient(to left, #e2e8f0, transparent)' }} />
            </div> */}

            {/* 6 Section Grid */}
            <div style={{ display: 'grid grid-cols-2 ' }}>

                {/* Section 1: Profiles & Leads */}
                <SectionPanel title="Profiles & Leads" icon="fa-user-plus" color="#6366f1" isTwoLine={true}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {/* Row 1: Sources */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px' }}>
                            <StatBadge label="Total Profiles" value={m.profiles_total} metricType="profiles-total" accent="#6366f1" />
                            <StatBadge label="Bulk Uploaded Interview" value={m.profiles_bulk} metricType="profiles-bulk" accent="#6366f1" />
                            <StatBadge label="Bulk Uploaded Clients" value={m.client_profiles_bulk} metricType="client-profiles-bulk" accent="#6366f1" />
                            <StatBadge label="Walk-in / Direct" value={m.profiles_walkin} metricType="profiles-walkin" accent="#6366f1" />
                            <StatBadge label="Website / Platform" value={m.profiles_website} metricType="profiles-website" accent="#6366f1" />
                            <StatBadge label="CRM Incoming" value={m.profiles_crm} metricType="profiles-crm" accent="#6366f1" />
                            <StatBadge label="Facebook / Social" value={m.profiles_facebook} metricType="profiles-facebook" accent="#6366f1" />
                            <StatBadge label="Self Added" value={m.profiles_self} metricType="profiles-self" accent="#6366f1" />
                        </div>
                        
                        {/* Horizontal Divider */}
                        <div style={{ height: '1.5px', background: 'linear-gradient(to right, transparent, #e2e8f0 10%, #e2e8f0 90%, transparent)', margin: '4px 0' }} />
                        
                        {/* Row 2: Assignments & Statuses */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px' }}>
                            <StatBadge label="Assigned to me" value={m.profiles_assigned_me} metricType="profiles-assigned-me" accent="#6366f1" />
                            <StatBadge label="Assigned to other" value={m.profiles_assigned_other} metricType="profiles-assigned-other" accent="#6366f1" />
                            <StatBadge label="Yet to contact" value={m.pending_yet_to_contact} metricType="pending-yet-to-contact" accent="#6366f1" />
                        </div>
                    </div>
                </SectionPanel>

                {/* Section 2: Calls Made */}
                <SectionPanel title="Interview Calls Made" icon="fa-phone-volume" color="#0ea5e9" isTwoLine={true}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <div style={{ width: '150px', flexShrink: 0 }}><StatBadge label="Total Calls Made" value={m.calls_total} metricType="lead-total-calls" accent="#0ea5e9" /></div>
                        <div style={{ width: '150px', flexShrink: 0 }}><StatBadge label="Fresh Interview Calls" value={m.lead_interview_calls} metricType="interview-calls" accent="#0ea5e9" /></div>
                        <div style={{ width: '150px', flexShrink: 0 }}><StatBadge label="Follow Up Done" value={m.lead_followup_calls} metricType="lead-followup-calls" accent="#0ea5e9" /></div>
                        
                        {/* Vertical Divider */}
                        <div style={{ width: '2px', alignSelf: 'stretch', background: 'linear-gradient(to bottom, transparent, #0ea5e9, transparent)', margin: '0 8px', minHeight: '60px' }} />
                        
                        <div style={{ width: '150px', flexShrink: 0 }}><StatBadge label="Connected Calls" value={m.connected_calls} metricType="connected-calls" accent="#0ea5e9" /></div>
                        <div style={{ width: '150px', flexShrink: 0 }}><StatBadge label="Not Connected" value={m.not_connected_calls} metricType="not-connected-calls" accent="#0ea5e9" /></div>
                        <div style={{ width: '150px', flexShrink: 0 }}><StatBadge label="Scheduled Interview Follow-Ups (Today)" value={m.scheduled_interview_followups} metricType="scheduled-interview-followups" accent="#0ea5e9" /></div>
                    </div>
                </SectionPanel>

                {/* Section 3: Interview Pipeline */}
                <SectionPanel title="Interview Pipeline" icon="fa-calendar-check" color="#10b981">
                    <StatBadge label="Total Projected (Scheduled)" value={m.scheduled_count} metricType="interview-scheduled" accent="#10b981" />
                    <StatBadge label="Overall Attended" value={m.interview_attended_total} metricType="interview-attended" accent="#10b981" />
                    <StatBadge label="Direct Walk-in" value={m.direct_walkins} metricType="direct-walkins" accent="#10b981" />
                    <StatBadge label="Scheduled Today" value={m.interview_scheduled_today} metricType="interview-scheduled-today" accent="#10b981" />
                    <StatBadge label="Scheduled Tomorrow" value={m.interview_scheduled_tomorrow} metricType="interview-scheduled-tomorrow" accent="#10b981" />
                    <StatBadge label="Scheduled Future" value={m.interview_scheduled_future} metricType="interview-scheduled-future" accent="#10b981" />
                    <StatBadge label="Scheduled Attended (Conducted)" value={m.scheduled_attended} metricType="scheduled-attended" accent="#10b981" />
                    <StatBadge label="Not Attended" value={m.interview_not_attended} metricType="interview-not-attended" accent="#10b981" />
                    <StatBadge label="Scheduled Not Attended Follow-ups" value={m.interview_followup_pending} metricType="interview-followup-pending" accent="#10b981" />
                </SectionPanel>


                {/* Section 4: Client Calls */}
                <SectionPanel title="Client Calls Made" icon="fa-briefcase" color="#f59e0b" isTwoLine={true}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <div style={{ width: '150px', flexShrink: 0 }}><StatBadge label="Total Client Calls" value={m.client_req_total} metricType="client-req-total" accent="#f59e0b" /></div>
                        <div style={{ width: '150px', flexShrink: 0 }}><StatBadge label="Fresh Client Calls" value={m.lead_client_calls} metricType="client-calls" accent="#f59e0b" /></div>
                        <div style={{ width: '150px', flexShrink: 0 }}><StatBadge label="Follow-up Done" value={m.client_req_followup} metricType="client-req-followup" accent="#f59e0b" /></div>
                        
                        {/* Vertical Divider */}
                        <div style={{ width: '2px', alignSelf: 'stretch', background: 'linear-gradient(to bottom, transparent, #f59e0b, transparent)', margin: '0 8px', minHeight: '60px' }} />
                        
                        <div style={{ width: '150px', flexShrink: 0 }}><StatBadge label="Prospects" value={m.client_req_prospect} metricType="client-req-prospect" accent="#f59e0b" /></div>
                        <div style={{ width: '150px', flexShrink: 0 }}><StatBadge label="Converted" value={m.client_req_converted} metricType="client-req-converted" accent="#f59e0b" /></div>
                        <div style={{ width: '150px', flexShrink: 0 }}><StatBadge label="Closed" value={m.client_req_closed} metricType="client-req-closed" accent="#f59e0b" /></div>
                        <div style={{ width: '150px', flexShrink: 0 }}><StatBadge label="Scheduled Client Follow-Ups (Today)" value={m.scheduled_client_followups} metricType="scheduled-client-followups" accent="#f59e0b" /></div>
                    </div>
                </SectionPanel>

                {/* Section 5: Interview Final Status */}
                <SectionPanel title="Interview Final Status" icon="fa-flag-checkered" color="#8b5cf6">
                    <StatBadge label="Offered" value={m.final_offered} metricType="final-offered" accent="#8b5cf6" />
                    <StatBadge label="Joined" value={m.final_joined} metricType="final-joined" accent="#8b5cf6" />
                    <StatBadge label="Rejected" value={m.rejected_count} metricType="rejected" accent="#8b5cf6" />
                    <StatBadge label="Closed" value={m.closed_count} metricType="closed" accent="#8b5cf6" />
                    <StatBadge label="Not Joined" value={m.final_not_joined} metricType="final-not-joined" accent="#8b5cf6" />
                    <StatBadge label="Rejected by Us" value={m.final_rejected_by_us} metricType="final-rejected-by-us" accent="#8b5cf6" />
                    <StatBadge label="Rej. by Candidate" value={m.final_rejected_by_candidate} metricType="final-rejected-by-candidate" accent="#8b5cf6" />
                </SectionPanel>

                {/* Section 6: Pending Actions */}
                <SectionPanel title="Pending Actions" icon="fa-hourglass-half" color="#ef4444">
                    <StatBadge label="Total Pending" value={m.pending_followups_total} metricType="pending-followups-total" accent="#ef4444" />
                    <StatBadge label="Interview Calls Pending" value={m.pending_followups_interview} metricType="pending-followups-interview" accent="#ef4444" />
                    <StatBadge label="Client Calls Pending" value={m.pending_followups_client} metricType="pending-followups-client" accent="#ef4444" />
                </SectionPanel>

            </div>
        </div>
    );
};

export default ActivityDashboard;